import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../../../services/api';
import { API_URL } from '../../../utils/api';
import Modal from '../../../components/Modal';

/**
 * AddAttendeesModal — bjud in fler deltagare till ett enskilt möte.
 * Hämtar projektmedlemmar (BK, IA, byggare, UE, projektör) och låter
 * användaren välja vilka som ska bjudas in till just DETTA möte.
 * Filtrerar automatiskt bort de som redan är inbjudna.
 */
const AddAttendeesModal = ({ isOpen, onClose, meeting, projectId, onSuccess }) => {
  const [members, setMembers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Hämta projektmedlemmar när modalen öppnas
  useEffect(() => {
    if (!isOpen || !projectId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        // /projektering är monterat utan /api-prefix, så fetch med full URL
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/projektering/${projectId}/members`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        setMembers(data?.members || []);
      } catch (err) {
        console.error('Kunde inte hämta medlemmar:', err);
        toast.error('Kunde inte hämta projektmedlemmar');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, projectId]);

  // Markera vilka som redan är inbjudna (istället för att filtrera bort)
  const existingAttendeeIds = new Set(
    (meeting?.attendees || [])
      .map(a => (a.userId?._id || a.userId)?.toString())
      .filter(Boolean)
  );
  const existingEmails = new Set(
    (meeting?.attendees || [])
      .map(a => (a.email || '').toLowerCase())
      .filter(Boolean)
  );
  const isAlreadyInvited = (m) => {
    const id = (m._id || m.id)?.toString();
    const email = (m.email || '').toLowerCase();
    if (id && existingAttendeeIds.has(id)) return true;
    if (email && existingEmails.has(email)) return true;
    return false;
  };
  const availableMembers = members; // visa alla, markera de inbjudna

  const toggleMember = (memberId) => {
    setSelectedIds(prev =>
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleInvite = async () => {
    if (selectedIds.length === 0) {
      toast.error('Välj minst en person att bjuda in');
      return;
    }
    setSaving(true);
    try {
      const chosen = members.filter(m => selectedIds.includes((m._id || m.id)?.toString()));
      const payload = {
        attendees: chosen.map(m => ({
          userId: m._id || m.id,
          email: m.email,
          name: `${m.firstName || ''} ${m.lastName || ''}`.trim() || m.email,
          role: m.role === 'inredningsarkitekt' ? 'architect'
              : m.role === 'byggkonsult' ? 'consultant'
              : m.role === 'byggare' ? 'builder'
              : m.role === 'UE' ? 'subcontractor'
              : m.role === 'projektor' ? 'external'
              : 'external'
        }))
      };
      await api.patch(`/meetings/${meeting._id}/add-attendees`, payload);
      toast.success(`${chosen.length} deltagare inbjudna`);
      onSuccess?.();
      onClose();
      setSelectedIds([]);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Kunde inte bjuda in deltagare');
    } finally {
      setSaving(false);
    }
  };

  const roleLabel = (role) => {
    const map = {
      'byggkonsult': 'Byggkonsult',
      'inredningsarkitekt': 'Inredningsarkitekt',
      'byggare': 'Byggare',
      'UE': 'Underentreprenör',
      'projektor': 'Projektör',
      'kund': 'Kund',
      'admin': 'Admin'
    };
    return map[role] || role;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="medium">
      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Bjud in fler deltagare</h3>
        <p className="text-sm text-gray-600 mb-4">
          Välj personer som ska bjudas in till detta möte.
          {meeting?.displayName && <span className="block mt-1 font-medium">{meeting.displayName}</span>}
        </p>

        {loading ? (
          <div className="py-8 text-center text-gray-500">Laddar medlemmar...</div>
        ) : availableMembers.length === 0 ? (
          <div className="py-8 text-center text-gray-500">
            Inga projektmedlemmar hittades.
          </div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {availableMembers.map(m => {
              const id = (m._id || m.id)?.toString();
              const isSelected = selectedIds.includes(id);
              const alreadyInvited = isAlreadyInvited(m);
              return (
                <button
                  key={id}
                  onClick={() => !alreadyInvited && toggleMember(id)}
                  disabled={alreadyInvited}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
                    alreadyInvited
                      ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                      : isSelected
                        ? 'border-leadme-pink bg-leadme-pink/5'
                        : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                    alreadyInvited
                      ? 'border-green-400 bg-green-400'
                      : isSelected ? 'border-leadme-pink bg-leadme-pink' : 'border-gray-300'
                  }`}>
                    {(isSelected || alreadyInvited) && (
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {`${m.firstName || ''} ${m.lastName || ''}`.trim() || m.email}
                      {alreadyInvited && (
                        <span className="ml-2 text-xs text-green-600 font-normal">Redan inbjuden</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {roleLabel(m.role)} · {m.email}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Avbryt
          </button>
          <button
            onClick={handleInvite}
            disabled={saving || selectedIds.length === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-leadme-pink rounded-lg hover:bg-leadme-pink/90 disabled:opacity-50"
          >
            {saving ? 'Bjuder in...' : `Bjud in (${selectedIds.length})`}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AddAttendeesModal;
